import React, { useCallback, useEffect, useState } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import classes from "./classes.module.scss";

type IProps = {
	onChange?: (input: string) => void;
	value?: string;
	placeholder?: string;
	disabled?: boolean;
	onClear?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
	/**
	 * Hauteur et typo réduites pour les listes en colonne (sidebar).
	 * Côté **`DefaultDashboardWithList`**, activer via **`sidebarCompactSearch={SIDEBAR_LIST_COMPACT_SEARCH}`** (**`dashboardListSidebarDefaults.ts`**).
	 */
	compact?: boolean;
};

function useSearchBarState(props: {
	propValue: string | undefined;
	onChange?: (input: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onClear?: () => void;
}): {
	value: string;
	handleOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleFocus: () => void;
	handleBlur: () => void;
	clearValue: () => void;
	isFocused: boolean;
} {
	const { propValue, onChange, onFocus, onBlur, onClear } = props;
	const [isFocused, setIsFocused] = useState(false);
	const [value, setValue] = useState(propValue || "");

	const changeValue = useCallback((v: string) => {
		setValue(v);
		onChange?.(v);
	}, [onChange]);

	const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => changeValue(e.target.value), [changeValue]);
	const handleFocus = useCallback(() => {
		setIsFocused(true);
		onFocus?.();
	}, [onFocus]);
	const handleBlur = useCallback(() => {
		setIsFocused(false);
		onBlur?.();
	}, [onBlur]);
	const clearValue = useCallback(() => {
		changeValue("");
		onClear?.();
	}, [changeValue, onClear]);

	useEffect(() => {
		if (propValue !== undefined) setValue(propValue);
	}, [propValue]);

	return { value, handleOnChange, handleFocus, handleBlur, clearValue, isFocused };
}

export default function SearchBar(props: IProps): React.JSX.Element {
	const { value: propValue, placeholder = "Rechercher", disabled = false, compact = false, ...rest } = props;
	const state = useSearchBarState({ propValue, ...rest });
	const iconSize = compact ? 18 : 24;

	return (
		<label
			className={classes["root"]}
			data-compact={compact}
			data-is-focused={state.isFocused}
			data-has-value={state.value !== ""}
			data-is-disabled={disabled}>
			<div className={classes["input-container"]}>
				<MagnifyingGlassIcon width={iconSize} height={iconSize} />
				<input
					type="text"
					value={state.value}
					placeholder={placeholder}
					className={classes["input"]}
					onChange={state.handleOnChange}
					onFocus={state.handleFocus}
					onBlur={state.handleBlur}
				/>
				{state.value !== "" && <XMarkIcon width={iconSize} height={iconSize} className={classes["cross"]} onClick={state.clearValue} />}
			</div>
		</label>
	);
}
